// Is this line actually working, and when did each system last hear from us.
//
// Kevin, 2026-08-10: "is there any troubleshooting information about the line that can be
// added?" The line already carried all of this and showed none of it — the first question on
// any "it's not working" call is whether the phone is registered, and answering it meant
// querying an API by hand.
//
// Online/offline IS registration — the mechanism keeps the SIP name, the operator does not.
// REGISTRATION IS KEYED BY SIP ACCOUNT ID, not by extension number. The map from
// /sip-registrations looks like it is keyed by extension and is not; reading it the obvious
// way returns "absent" for every line, which reads as "not registered" and is not
// (2026-08-10). A known-good control proved the keying before this shipped.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
// THIS CARD IS WHERE THE TIMESTAMP BUG DID ITS REAL DAMAGE. Its own `when()` did
// `new Date(ts)` on D1's zone-less UTC string, which V8 reads as local time — six hours
// ahead in MDT — and then subtracted it from now. A negative age is less than a minute, so
// every row on this card said "just now" whatever it actually was: a push from last week
// reported as current, on the one card an operator reads to answer "is this line healthy".
// See lib/time.js.
import { ago as when } from '../../lib/time.js';
import LineSipHistoryDialog from './LineSipHistoryDialog.jsx';

function Row({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-right text-sm text-gray-900">{children}</dd>
    </div>
  );
}

// A fact BELONGING to the row above it, in this rail's width.
//
// Kevin, 2026-08-19: "the PBX info is all jammed together and hard to read." The host's six
// numbers were joined with dots into one right-aligned run-on — "agent 0.37.0+aca7a89 · AMI ok ·
// DB ok · 116 extensions · disk 22% · up 1d" — wrapped across three lines in a 230px rail, with
// no label anywhere near the number it belonged to. Reading the disk figure meant parsing the
// sentence.
//
// One fact per line, label left and value right, like every other row on this card. Indented and
// a size down so they read as the host's details rather than as six more line-level checks. Six
// short rows are longer on screen than three wrapped ones and quicker to read, which is the
// trade this card is for.
function SubRow({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 pl-3">
      <dt className="text-[11px] text-gray-400">{label}</dt>
      <dd className="text-right text-xs text-gray-700">{children}</dd>
    </div>
  );
}

// "up 1d" on a host restarted four hours ago rounded to "up 0d". Days once there is a day.
/**
 * The line's ring groups, Verso's record beside the host's.
 *
 * WHY BOTH. Reporting only Verso's `sync_status` is a record of our last WRITE rather than an
 * observation of the host — the mistake that let a suspend report success on a PBX it had never
 * reached. So each group shows what Verso believes, what the host holds, and whether they agree.
 *
 * AND `agrees == null` IS ITS OWN STATE. An unreadable host is not a disagreement, and rendering
 * "we did not look" as "they differ" would send somebody to fix a group that is fine.
 */
function RingGroupRows({ data }) {
  if (data.isLoading) {
    return <Row label="Ring groups"><span className="text-gray-400">checking…</span></Row>;
  }
  if (data.error) {
    return (
      <Row label="Ring groups">
        <span className="text-amber-800">
          Could not read them, so this says nothing about whether they match the phone system:{' '}
          {data.error.body?.error || data.error.message}
        </span>
      </Row>
    );
  }
  const groups = data.data?.groups || [];
  if (!groups.length) return null;   // A line with no groups says nothing rather than "none".

  return (
    <>
      <Row label="Ring groups">
        <span className="text-gray-900">{groups.length}</span>
        {!data.data?.host_readable && (
          <span className="ml-2 text-amber-800">— the phone system could not be read, so none of these are confirmed</span>
        )}
      </Row>
      {groups.map((g) => {
        const side = g.host || g.verso || {};
        const members = side.members || [];
        return (
          <SubRow key={g.grpnum} label={`${g.name || 'unnamed'} · ${g.grpnum}`}>
            <span className="text-gray-500">{g.role === 'primary' ? 'the number rings this' : 'a menu key opens this'}</span>
            {' · '}
            {members.length
              ? `rings ${members.join(', ')} for ${side.ring_time ?? '?'}s`
              : /* An empty grplist EMPTIES a live group, so a department with no members is a real
                   fault rather than a blank field. */
                <span className="font-medium text-red-700">rings nobody</span>}
            {side.no_answer && <> · no answer → {(side.no_answer.label || '').toLowerCase()}</>}
            {g.agrees === false && (
              <div className="text-amber-800">
                {g.only_in_verso
                  ? 'Verso has this group and the phone system does not.'
                  : 'Verso and the phone system disagree about this group.'}
              </div>
            )}
            {g.agrees === null && data.data?.host_readable && (
              <div className="text-gray-500">Verso has no record of this one — it exists only on the phone system.</div>
            )}
          </SubRow>
        );
      })}
    </>
  );
}

function fmtUptime(seconds) {
  if (seconds == null) return null;
  const h = Math.floor(seconds / 3600);
  if (h < 1) return `${Math.max(1, Math.floor(seconds / 60))}m`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function LineDiagnostics({ line }) {
  // EVERY RING GROUP THIS LINE OWNS — its own plus one per department. Kevin, 2026-09-02: the
  // numbers need no particular shape "as long as they are tracked and included in some
  // diagnostic/troubleshooting card". Before this they were tracked in `ring_groups` and in
  // route_links and shown nowhere, which is the same as untracked from an operator's seat.
  const ringGroups = useQuery({
    queryKey: ['line', String(line.id), 'ring-groups'],
    queryFn: () => api.get(`/lines/${line.id}/ring-groups`),
    retry: false,
  });

  const exts = useQuery({
    queryKey: ['line', String(line.id), 'extensions'],
    queryFn: () => api.get(`/lines/${line.id}/extensions`),
  });

  const regs = useQuery({
    queryKey: ['sip-registrations'],
    queryFn: () => api.get('/sip-registrations'),
    refetchInterval: 60_000,
  });

  // CALL TRACING (agent v0.39.0). Kevin, 2026-08-22: a button here to turn it on and off.
  //
  // A trace is verbose SIP logging on a production host, so it expires on the agent's side and this
  // only ever asks for one scoped to THIS line's extensions. Polled while it runs so the countdown
  // is the host's remaining seconds rather than a timer this page invented — a page that counted
  // down on its own would keep counting after the agent had stopped.
  const qc = useQueryClient();
  const [traceErr, setTraceErr] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const trace = useQuery({
    queryKey: ['line', String(line.id), 'trace'],
    queryFn: () => api.get(`/lines/${line.id}/trace`),
    enabled: Boolean(line.pbxAgentId),
    // Only while something is running. Idle, this is a button and does not need a heartbeat.
    refetchInterval: (q) => (q.state.data?.trace?.running ? 5_000 : false),
    retry: false,
  });
  // `active`, THE FIELD THE AGENT ACTUALLY SENDS. Verified against a live trace:
  //   idle    { active: false, v: 1 }
  //   running { active: true, secondsRemaining: 300, scope: '65.113.156.217', sip: true, ... }
  // This read `running`, which the payload has never contained — so the card said "off" through a
  // live trace, which is the exact failure the unreachable-host branch below exists to prevent,
  // arrived at by guessing a field name instead of looking. `running` is still tolerated in case a
  // later agent renames it back.
  const t = trace.data?.trace;
  const running = t?.active === true || t?.running === true;
  // "We could not ask" is not "off" — an operator who reads silence as stopped believes they have
  // switched off logging that is still going.
  const traceUnknown = trace.isError || trace.data?.unknown === true;
  // NOT SUPPORTED IS NOT UNREACHABLE. The route answers a capability refusal (409,
  // `tracing_unsupported`) on a host with no AMI — an Aria host has its own SIP stack — and that
  // must not be folded into `traceUnknown`, which means "we could not ask". One reads as "there is
  // nothing to switch on here", the other as "we do not know whether logging is running".
  const traceSupported = trace.error?.body?.code === 'tracing_unsupported'
    ? false
    : (trace.data?.code === 'tracing_unsupported' ? false : true);
  const remaining = t?.secondsRemaining ?? t?.seconds_remaining ?? null;
  // What the host is ACTUALLY watching. It resolves an extension to the address that extension
  // registers from and scopes the logger there, so this is more honest than repeating back the
  // extension we asked about.
  const traceScope = t?.scope ?? t?.extension ?? null;

  const startTrace = useMutation({
    mutationFn: () => api.post(`/lines/${line.id}/trace`, { seconds: 300 }),
    onSuccess: () => { setTraceErr(null); qc.invalidateQueries({ queryKey: ['line', String(line.id), 'trace'] }); },
    onError: (e) => setTraceErr(e?.message || 'could not start the trace'),
  });
  const stopTrace = useMutation({
    mutationFn: () => api.delete(`/lines/${line.id}/trace`),
    onSuccess: () => { setTraceErr(null); qc.invalidateQueries({ queryKey: ['line', String(line.id), 'trace'] }); },
    onError: (e) => setTraceErr(e?.message || 'could not stop the trace'),
  });
  const busy = startTrace.isPending || stopTrace.isPending;

  // Jitter/loss/RTT for calls in progress. Needs NO trace, which is why it is worth showing on its
  // own: "crackly audio" is answered by numbers before anybody turns logging on.
  const quality = useQuery({
    queryKey: ['line', String(line.id), 'live-quality'],
    queryFn: () => api.get(`/lines/${line.id}/live-quality`),
    enabled: Boolean(line.pbxAgentId),
    refetchInterval: running ? 5_000 : 30_000,
    retry: false,
  });

  // Already filtered to this line by the worker — `pjsip show history` is the whole host's ring and
  // the raw string never leaves it.
  const sip = quality.data?.sip_history ?? null;

  // THE HOST'S OWN STATE, and GDMS's view of the phones (2026-08-19).
  //
  // Kevin: "diagnostics tab can be expanded to include registration and device state and any other
  // stats that might be available from the PBX host."
  //
  // This is the set that actually answered a real question earlier today. Art Caruso's extension was
  // registered while GDMS reported his ATA offline, and the two facts live in different systems: the
  // PBX knows the SIP contact and its round-trip time, GDMS knows whether the device is talking to
  // Grandstream's cloud and when it last did. Neither alone explains the pair, and reading them
  // meant hand-calling two proxy endpoints. Now the page does it.
  const health = useQuery({
    queryKey: ['agent', line.pbxAgentId, 'health'],
    queryFn: () => api.get(`/agents/${line.pbxAgentId}/proxy/health`),
    enabled: Boolean(line.pbxAgentId),
    staleTime: 30_000,
  });

  // WHETHER FREEPBX IS SERVING WHAT IT WAS TOLD, which is a different question from whether the
  // agent is up — and the one that affects calls.
  //
  // Kevin, 2026-08-20: "pbx host uptimes are 2 hrs?" then "oh, that is the agent uptime, not
  // freepbx's?" It was, and the panel called it plain "Uptime" under the host's name. The agent had
  // restarted for the 0.38.0 rollout; FreePBX had not. But the possibility worth ruling out was the
  // other one: `/etc/cont-init.d/10-freepbx` recreates `/tmp/cron.error` as root on every container
  // start, and its severe variant aborts `fwconsole reload` before regenerating any config — so a
  // build can report every step confirmed while Asterisk serves the old dialplan (AGENT_ASKS #17).
  //
  // Only askable since agent 0.38.0. An older host answers nothing and the row stays absent, which
  // is right: absent is not "no reload pending".
  const readiness = useQuery({
    queryKey: ['pbx-host', line.pbxAgentId, 'readiness'],
    queryFn: () => api.get(`/pbx-hosts/${line.pbxAgentId}/readiness`),
    enabled: Boolean(line.pbxAgentId),
    staleTime: 30_000,
  });

  // WHAT THE CARRIER HOLDS FOR MESSAGING, which no screen could show.
  //
  // Kevin, 2026-08-19: "the operator doesn't need to be burdened by this information but it would be
  // good to include in the diagnostic card." The two facts are genuinely different and were being
  // conflated: Commio's messaging setup is one-time provisioning that stays in place, and the line's
  // switch is Verso's black hole. So the switch says what happens to a text, and this says what the
  // carrier is doing — which is how somebody answers "is Commio still forwarding?" without a
  // hand-rolled API call.
  //
  // Live per number, not from `features_json`: the stored blob comes from the DID list row at import,
  // which carries no messaging fields at all, and reading it is what made the Messaging tab claim
  // "off at the carrier" over an enabled number.
  const carrierSms = useQuery({
    queryKey: ['did', String(line.number || '').replace(/^\+/, ''), 'commio-live'],
    queryFn: () => api.get(`/dids/${encodeURIComponent(String(line.number).replace(/^\+/, ''))}/commio-live`),
    enabled: Boolean(line.number),
    staleTime: 60_000,
    retry: false,
  });

  const rings = exts.data?.extensions || [];
  const map = regs.data?.registrations || {};

  // Only what we actually asked about. An extension missing from the map is "we did not get
  // an answer for it", which is not the same as unregistered.
  const seen = rings.map((e) => ({ ...e, reg: map[String(e.sipAccountId)] ?? null }));
  const up = seen.filter((e) => e.reg?.registered).length;
  const known = seen.filter((e) => e.reg != null).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-sm font-semibold text-gray-900">Diagnostics</h2>
        {regs.data?.cached && (
          // Say when the answer is a snapshot rather than a live read.
          <span className="text-[10px] text-gray-400">cached</span>
        )}
      </div>

      {regs.error && (
        <p className="mt-3 text-sm text-amber-800">
          Could not read registrations, so this says nothing about whether the phones are up:{' '}
          {regs.error.body?.error || regs.error.message}
        </p>
      )}

      <dl className="mt-3 divide-y divide-gray-100">
        <RingGroupRows data={ringGroups} />
        <Row label="Online">
          {regs.isLoading || exts.isLoading ? (
            <span className="text-gray-400">checking…</span>
          ) : !rings.length ? (
            <span className="text-gray-500">no extensions</span>
          ) : known === 0 ? (
            <span className="text-gray-500">the PBX did not answer</span>
          ) : (
            <>
              <span className={up === known ? 'text-green-700' : up === 0 ? 'text-red-700' : 'text-amber-700'}>
                {up} of {known}
              </span>
              {known < rings.length && (
                <span className="ml-1 text-xs text-gray-500">
                  ({rings.length - known} not answered for)
                </span>
              )}
            </>
          )}
        </Row>

        {seen.map((e) => (
          <Row key={e.sipAccountId} label={e.username}>
            {e.reg == null ? (
              <span className="text-gray-500">no answer</span>
            ) : e.reg.registered ? (
              <span className="text-green-700">
                online
                {e.reg.state && e.reg.state !== 'Not in use' && (
                  <span className="ml-1 text-xs text-gray-500">· {e.reg.state}</span>
                )}
                {/* THE CONTACT, which the registration payload has always carried and this panel
                    never showed. "Avail" with a round-trip time is the difference between a phone
                    the PBX can actually reach and a stale registration — and it is what proved Art
                    Caruso's ATA was alive while GDMS called it offline. */}
                {e.reg.contactStatus && e.reg.contactStatus !== 'Avail' && (
                  <span className="ml-1 text-xs text-amber-700">· contact {e.reg.contactStatus}</span>
                )}
                {e.reg.contactRttMs != null && (
                  <span className="ml-1 text-xs text-gray-500">
                    · {Math.round(e.reg.contactRttMs)}ms
                  </span>
                )}
              </span>
            ) : (
              <span className="text-red-700">offline</span>
            )}
          </Row>
        ))}

        <Row label="Built">
          {line.provisionedAt
            ? <>{when(line.provisionedAt)}</>
            : <span className="text-amber-800">never</span>}
        </Row>
        <Row label="Extension pushed to the PBX">
          {line.extensionSyncedAt
            ? when(line.extensionSyncedAt)
            : <span className="text-amber-800">never</span>}
        </Row>
        {/* PER EXTENSION, not the line's primary. Reading line.deviceSyncedAt said "no phone
            bound" on a line whose three registered extensions sit on a DP750 — the same
            single-extension leak that made the old Phone card wrong (seen live 2026-08-11). */}
        <Row label="Config pushed to the phone">
          {(() => {
            // A Calix is excluded from the count entirely: it has no config to be pushed,
            // so counting it as one of N "on a phone" makes a complete line read as
            // incomplete forever (Carole Carroll, 2026-08-17).
            const withPhone = seen.filter((e) => e.deviceMac && !e.deviceSelfProvisioning);
            if (!withPhone.length) {
              // "No phone bound" would be a second wrong answer: a Calix IS bound, it just
              // takes its config from the ONT side rather than from us. Say which.
              const selfOnly = seen.some((e) => e.deviceMac && e.deviceSelfProvisioning);
              return (
                <span className="text-gray-500">
                  {selfOnly ? 'not needed — the router configures itself' : 'no phone bound'}
                </span>
              );
            }
            const never = withPhone.filter((e) => !e.deviceSyncedAt);
            const newest = withPhone
              .map((e) => e.deviceSyncedAt)
              .filter(Boolean)
              .sort()
              .pop();
            return (
              <>
                {newest ? when(newest) : <span className="text-amber-800">never</span>}
                <span className="ml-1 text-xs text-gray-500">
                  · {withPhone.length} of {seen.length} on a phone
                </span>
                {never.length > 0 && newest && (
                  <span className="block text-xs text-amber-800">
                    {never.length} never given its config
                  </span>
                )}
              </>
            );
          })()}
        </Row>
        {/* MESSAGING, on both sides of the line. The gate is ours and decides delivery; the carrier
            row is provisioning and is expected to stay on once set. */}
        {line.number && (
          <Row label="Messaging">
            {line.smsEnabled === 1
              ? <span className="text-green-700">on for this line</span>
              : <span className="text-gray-500">
                  {line.smsEnabled === 0 ? 'off — texts discarded' : 'never switched on'}
                </span>}
          </Row>
        )}
        {line.number && (
          <SubRow label="At the carrier">
            {carrierSms.isLoading ? (
              <span className="text-gray-400">checking…</span>
            ) : carrierSms.error || carrierSms.data?.messaging?.readable === false ? (
              // Could not ask is not "off" — the distinction this whole card exists for.
              <span className="text-gray-500">could not read Commio</span>
            ) : carrierSms.data?.messaging?.sms_enabled ? (
              <>
                enabled
                {carrierSms.data.messaging.sms_routing_profile_id != null ? (
                  <span className="ml-1 text-gray-400">
                    · profile {carrierSms.data.messaging.sms_routing_profile_id}
                  </span>
                ) : (
                  // ENABLED AND POINTED AT NOTHING is a real state, and it is not the same as
                  // enabled. Carole Carroll's +13038236693 sits in it: Commio accepts texts for the
                  // number and forwards them nowhere, so nothing can reach Verso however the line's
                  // gate is set. Switching messaging on for the line provisions the profile.
                  <span className="ml-1 text-amber-700">· forwards nowhere</span>
                )}
              </>
            ) : (
              'not enabled'
            )}
          </SubRow>
        )}

        {/* WHAT THE HOST ITSELF SAYS. Read live from the agent, so an unreachable host shows as
            unreachable rather than as an absence — the distinction this panel exists for. */}
        {line.pbxAgentId && (
          <Row label={line.pbxName || 'PBX host'}>
            {health.isLoading ? (
              <span className="text-gray-400">checking…</span>
            ) : health.error ? (
              <span className="text-red-700">did not answer</span>
            ) : (
              <span className={health.data?.ok ? 'text-green-700' : 'text-amber-700'}>
                {health.data?.ok ? 'healthy' : 'degraded'}
              </span>
            )}
          </Row>
        )}
        {/* The host's own numbers, one per line under it. A service that is DOWN is coloured;
            the rest are plain, so the eye lands on the one that matters. */}
        {line.pbxAgentId && !health.isLoading && !health.error && (
          <>
            {health.data?.version && (
              <SubRow label="Agent">
                <span className="font-mono text-[11px]">{health.data.version}</span>
              </SubRow>
            )}
            {health.data?.ami && (
              <SubRow label="AMI">
                <span className={health.data.ami.ok ? undefined : 'text-red-700'}>
                  {health.data.ami.ok ? 'ok' : 'down'}
                </span>
              </SubRow>
            )}
            {health.data?.mariadb && (
              <SubRow label="Database">
                <span className={health.data.mariadb.ok ? undefined : 'text-red-700'}>
                  {health.data.mariadb.ok ? 'ok' : 'down'}
                </span>
              </SubRow>
            )}
            {health.data?.mariadb?.extensions != null && (
              <SubRow label="Extensions on host">{health.data.mariadb.extensions}</SubRow>
            )}
            {health.data?.disk?.usedPct != null && (
              <SubRow label="Disk used">
                {/* Amber before it bites: a full disk stops call recording and MySQL both. */}
                <span className={health.data.disk.usedPct >= 85 ? 'text-amber-800' : undefined}>
                  {health.data.disk.usedPct}%
                </span>
              </SubRow>
            )}
            {/* FREEPBX'S OWN STATE, above the agent's uptime, because it is the one that decides
                whether a call behaves as configured. `pending: true` means config was written and
                the reload has not regenerated it — or the stale /tmp/cron.error defect, which the
                agent says so rather than guessing between them. */}
            {readiness.data?.reload?.pendingReload?.known === true && (
              <SubRow label="PBX config">
                {readiness.data.reload.pendingReload.pending
                  ? (
                    <span className="text-amber-800">
                      reload pending — Asterisk may still be serving the old dialplan
                    </span>
                  )
                  : 'loaded'}
              </SubRow>
            )}
            {/* NAMED FOR WHAT IT MEASURES. This said "Uptime" under the host's name, so a
                two-hour agent restart read as the PBX having rebooted. */}
            {health.data?.uptime_seconds != null && (
              <SubRow label="Agent uptime">{fmtUptime(health.data.uptime_seconds)}</SubRow>
            )}
          </>
        )}

        {/* CALL TRACING. Last on the card because it is the thing you reach for when the rows above
            have not answered the question. */}
        {line.pbxAgentId && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <Row label="Calls in progress">
              {quality.isError ? <span className="text-gray-400">—</span>
                : quality.data ? (quality.data.calls_in_progress ?? 0) : '…'}
            </Row>
            {/* The numbers that need no trace. Only rendered when a call is up, because zeros for
                jitter and loss on an idle line invite being read as a perfect connection. */}
            {(quality.data?.channels || []).map((c) => (
              <SubRow key={c.channel} label={String(c.channel).replace(/^[A-Za-z0-9]+\//, '')}>
                {[
                  c.rtt != null ? `rtt ${c.rtt}ms` : null,
                  c.jitter != null ? `jitter ${c.jitter}ms` : null,
                  (c.loss ?? c.packet_loss) != null ? `loss ${c.loss ?? c.packet_loss}%` : null,
                ].filter(Boolean).join(' · ') || 'up'}
              </SubRow>
            ))}

            <Row label="Call tracing">
              {traceUnknown ? (
                // Deliberately not "off".
                <span className="text-amber-700">could not ask the host</span>
              ) : running ? (
                <span className="text-amber-700">
                  on{remaining != null ? ` · ${remaining}s left` : ''}
                </span>
              ) : (
                <span className="text-gray-500">off</span>
              )}
            </Row>
            <div className="pt-1">
              {/* NOT OFFERED WHERE THERE IS NOTHING TO TRACE. `call-tracing` needs AMI, and an Aria
                  host has its own SIP stack rather than Asterisk — so pressing this on pbx-04
                  answered `POST /diagnostics/trace → 404 Not Found`, a raw transport error handed
                  to an operator as if the host were broken. Live on 2026-09-12, straight after a
                  clean migration onto that host. Not applicable is not failure. */}
              {traceSupported === false ? (
                <p className="text-[11px] leading-tight text-gray-500">
                  SIP tracing isn’t available on {line.pbxName || 'this PBX'} — it has its own SIP
                  stack rather than Asterisk. Call history and live quality still work.
                </p>
              ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => (running ? stopTrace.mutate() : startTrace.mutate())}
                className={`w-full rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-50 ${
                  running
                    ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {busy ? 'Working…' : running ? 'Stop tracing' : 'Trace this line for 5 min'}
              </button>
              )}
              {/* The scope, said before it is switched on rather than after. This writes verbose
                  logging to a host carrying every other customer, and the reassurance that it is
                  bounded is worth more than the reassurance that it worked. */}
              {!running && !traceUnknown && (
                <p className="mt-1 text-[10px] leading-tight text-gray-400">
                  {/* ONE extension, because the agent has no global trace — logging every SIP
                      message on the host would flood the log its own /logs endpoint serves. On a
                      ring group that means one of the phones, and saying which beats implying all
                      of them are covered. */}
                  SIP logging for {(exts.data?.extensions || []).length > 1
                    ? 'this line’s primary extension'
                    : 'this line’s extension'} only. Expires on its own.
                </p>
              )}
              {/* What the host actually took, once it has taken it. */}
              {running && traceScope && (
                <p className="mt-1 font-mono text-[10px] leading-tight text-gray-400">
                  tracing {traceScope}
                </p>
              )}
              {traceErr && <p className="mt-1 text-[10px] text-red-600">{traceErr}</p>}

              {/* WHAT THE TRACE PRODUCED. Kevin, 2026-08-22: "what do you do after a trace has been
                  run on a line?" Before this, nothing — it was started, stopped, and the evidence
                  discarded. The findings are the sentences worth reading; the messages are for when
                  they are not enough. */}
              {sip && sip.matched > 0 && (
                <div className="mt-2">
                  {sip.findings?.map((f) => (
                    <p
                      key={f.text}
                      className={`text-[10px] leading-snug ${
                        f.level === 'warn' ? 'text-amber-800' : 'text-gray-500'}`}
                    >
                      {f.text}
                    </p>
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowHistory(true)}
                    className="mt-1 text-[10px] text-blue-600 underline"
                  >
                    {`Show ${sip.matched} SIP message${sip.matched === 1 ? '' : 's'}`}
                  </button>
                </div>
              )}
              {sip && sip.matched === 0 && running && (
                <p className="mt-2 text-[10px] text-gray-400">
                  No SIP for this line in the ring yet — place or receive a call while the trace runs.
                </p>
              )}
            </div>
          </div>
        )}

      </dl>

      {showHistory && (
        <LineSipHistoryDialog
          line={line}
          sip={sip}
          extensions={quality.data?.extensions || []}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
